import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";
import EmptyState from "@/components/feature/EmptyState";
import { Breadcrumb } from "@/components/feature/Pagination";
import ChapterList from "@/pages/detail/components/ChapterList";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { formatCount } from "@/utils/format";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useDocumentTitle(
    comic ? `${comic.title} - 老司机漫画` : "漫画详情 - 老司机漫画",
    comic ? `${comic.title}（${comic.author}）简介：${comic.description}` : undefined,
  );

  const related = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.genres.some((g) => comic.genres.includes(g)))
      .sort((a, b) => b.favorites - a.favorites)
      .slice(0, 5);
  }, [comic]);

  if (!comic) {
    return (
      <main className="w-full px-4 md:px-6 py-10">
        <EmptyState
          title="未找到该漫画"
          desc="这部漫画可能不存在或已被移除，去分类页看看其他精彩作品吧。"
        />
      </main>
    );
  }

  const favorited = isFavorite(comic.id);
  const firstChapter = comic.chapters[0];

  return (
    <main className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", path: "/" },
          { label: "漫画分类", path: "/category" },
          { label: comic.title },
        ]}
      />

      <section className="mt-5 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">
        <div className="flex flex-col gap-4">
          <div className="w-40 md:w-52 lg:w-full rounded-xl overflow-hidden border border-background-200 bg-background-200 self-center lg:self-auto">
            <div className="w-full aspect-[3/4]">
              <img
                src={comic.cover}
                alt={`${comic.title} 封面`}
                title={`${comic.title} 漫画封面`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <Link
            to={firstChapter ? `/read/${comic.id}/${firstChapter.id}` : "#"}
            className="hidden lg:inline-flex items-center justify-center gap-2 h-12 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-fill text-lg"></i>
            开始阅读
          </Link>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`px-2.5 py-1 rounded-md text-xs font-medium ${
                comic.status === "连载"
                  ? "bg-primary-500 text-background-50"
                  : "bg-secondary-700 text-background-50"
              }`}
            >
              {comic.status}
            </span>
            {comic.genres.map((g) => (
              <Link
                key={g}
                to={`/category?genre=${encodeURIComponent(g)}`}
                className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {g}
              </Link>
            ))}
          </div>

          <h1 className="mt-3 font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
            {comic.title}
          </h1>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="text-xs text-foreground-500">作者</p>
              <p className="mt-1 text-sm font-medium text-foreground-900 truncate">{comic.author}</p>
            </div>
            <div className="p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="text-xs text-foreground-500">评分</p>
              <p className="mt-1 text-sm font-medium text-foreground-900 flex items-center gap-1">
                <i className="ri-star-fill text-primary-500"></i>
                {comic.rating.toFixed(1)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="text-xs text-foreground-500">收藏</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">
                {formatCount(comic.favorites)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-background-50 border border-background-200">
              <p className="text-xs text-foreground-500">最近更新</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{comic.updateTime}</p>
            </div>
          </div>

          <div className="mt-6">
            <h2 className="font-heading font-bold text-base text-foreground-950 mb-2">内容简介</h2>
            <p className="text-sm md:text-base leading-relaxed text-foreground-700">
              {comic.description}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(comic.id)}
              className={`inline-flex items-center gap-2 h-11 px-6 rounded-md font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                favorited
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "bg-background-50 border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-600"
              }`}
            >
              <i className={favorited ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"}></i>
              {favorited ? "已收藏" : "加入收藏"}
            </button>
            <Link
              to={firstChapter ? `/read/${comic.id}/${firstChapter.id}` : "#"}
              className="lg:hidden inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              开始阅读
            </Link>
            <p className="text-xs text-foreground-400 w-full sm:w-auto">
              <i className="ri-information-line mr-1"></i>
              收藏为本地演示，仅保存在你的浏览器中
            </p>
          </div>
        </div>
      </section>

      <ChapterList comicId={comic.id} chapters={comic.chapters} />

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading font-bold text-lg md:text-xl text-foreground-950 mb-5 flex items-center gap-2">
            <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-600 flex items-center justify-center">
              <i className="ri-magic-line text-xl"></i>
            </span>
            相关推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {related.map((item) => (
              <ComicCard key={item.id} comic={item} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}